import React, { useState, useCallback } from 'react';
import { Icon, Popover, Spin, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement } from 'chart.js';
import api from '../../../../services/apiInstance';
import Colors from '../../../../themes/Colors';

ChartJS.register(ArcElement);

// Maps each backend price-breakdown key to its i18n label key.
// The backend only returns { key, amount }; the frontend owns the label.
const ITEM_LABELS = {
  media_investment: 'Media investment',
  latinad_commission: 'LatinAd commission',
  partner_commission: 'Partner commission',
  dsp_commission: 'DSP commission',
  exchange_commission: 'Exchange commission',
  agency_commission: 'Agency commission',
  taxes: 'Taxes',
  national_taxes: 'National taxes',
  international_taxes: 'International taxes'
};

const CONCEPT_COLOR_SCALE = {
  agency_commission: { factor: 0.28, whiteBlend: 0.55 },
  exchange_commission: { factor: 0.39, whiteBlend: 0.46 },
  partner_commission: { factor: 0.5, whiteBlend: 0.38 },
  latinad_commission: { factor: 0.61, whiteBlend: 0.29 },
  international_taxes: { factor: 0.72, whiteBlend: 0.2 },
  media_investment: { factor: 1, whiteBlend: 0 }
};

const hexToRgb = hex => {
  const value = hex.replace('#', '');
  return {
    r: parseInt(value.substring(0, 2), 16),
    g: parseInt(value.substring(2, 4), 16),
    b: parseInt(value.substring(4, 6), 16)
  };
};

const rgbToHex = ({ r, g, b }) => {
  const channel = n => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
};

const isLightColor = hex => {
  const { r, g, b } = hexToRgb(hex);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 128;
};

const darkenByFactor = (hex, factor) => {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex({ r: r * factor, g: g * factor, b: b * factor });
};

const blendWithWhite = (hex, whiteBlend) => {
  const { r, g, b } = hexToRgb(hex);
  const mix = channel => channel + (255 - channel) * whiteBlend;
  return rgbToHex({ r: mix(r), g: mix(g), b: mix(b) });
};

const primaryIsLight = isLightColor(Colors.primary);

const getConceptColor = key => {
  const scale = CONCEPT_COLOR_SCALE[key] || CONCEPT_COLOR_SCALE.media_investment;
  if (scale.factor === 1) return Colors.primary;
  return primaryIsLight
    ? darkenByFactor(Colors.primary, scale.factor)
    : blendWithWhite(Colors.primary, scale.whiteBlend);
};

const DONUT_SIZE = 174;

const donutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '72%',
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false }
  },
  elements: { arc: { borderWidth: 0 } }
};

// The "includes adjustment rate" note hangs off the LatinAd commission, and is
// only shown when the campaign is billed through an agency *and* an exchange.
const ADJUSTMENT_NOTE_ON = 'latinad_commission';
const ADJUSTMENT_NOTE_REQUIRES = ['agency_commission', 'exchange_commission'];

// National taxes arrive flagged as `tax: true` and are listed after the
// subtotal instead of inside the cost section.
const isTax = item => item.tax === true;

const getAmount = item => Number(item.amount) || 0;

// Cost rows carry their share in `percentage`; tax rows their rate in `tax_percentage`.
const getPercentage = item => (isTax(item) ? item.tax_percentage : item.percentage);

const formatAmount = (amount, currency) => {
  const formatter = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2
  });
  return formatter.format(Number(amount) || 0);
};

const formatPercentage = percentage => `${Number(percentage.toFixed(2))}%`;

const BreakdownRow = ({
  label,
  amount,
  percentage,
  currency,
  bold,
  separator,
  note,
  dotColor,
  spacious
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 24,
      padding: spacious ? '6px 0' : '4px 0',
      fontWeight: bold ? 600 : 400,
      borderTop: separator ? `1px solid ${Colors.gray}` : 'none',
      marginTop: separator ? 4 : 0
    }}
  >
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {dotColor && (
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: dotColor,
            flexShrink: 0
          }}
        />
      )}
      <span>
        {label}
        {typeof percentage === 'number' && ` (${formatPercentage(percentage)})`}
        {note}
      </span>
    </span>
    <span style={{ whiteSpace: 'nowrap' }}>{formatAmount(amount, currency)}</span>
  </div>
);

/**
 * PriceBreakdownTooltip
 *
 * Renders an info icon next to a campaign price. On first hover it fetches
 * GET /campaigns/{campaignId}/price-breakdown and shows the price components
 * already filtered and ordered by the backend according to the current user's
 * role / company type.
 *
 * The breakdown is laid out in two blocks: the costs (commissions, international
 * taxes and media investment) adding up to `total_price_with_profit`, and the
 * taxes flagged with `tax: true`, which bring the figure up to
 * `total_price_with_tax`.
 */
function PriceBreakdownTooltip({ campaignId, currency }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [fetched, setFetched] = useState(false);

  const fetchBreakdown = useCallback(async () => {
    setLoading(true);
    setError(false);
    const response = await api.getCampaignPriceBreakdown(campaignId);
    if (response.ok && response.data && response.data.data) {
      setData(response.data.data);
    } else {
      setError(true);
    }
    setLoading(false);
    setFetched(true);
  }, [campaignId]);

  const handleVisibleChange = useCallback(
    visible => {
      if (visible && !fetched && !loading) {
        fetchBreakdown();
      }
    },
    [fetched, loading, fetchBreakdown]
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ minWidth: 200, textAlign: 'center', padding: 8 }}>
          <Spin size="small" />
        </div>
      );
    }

    if (error || !data) {
      return (
        <div style={{ minWidth: 200, padding: 4 }}>
          {t('Could not load the price breakdown')}
        </div>
      );
    }

    const resolvedCurrency = data.currency || currency;
    // The backend already returns the items in display order — never re-sort them.
    const items = (data.items || []).filter(item => getAmount(item) !== 0);
    const costItems = items.filter(item => !isTax(item));
    const taxItems = items.filter(isTax);

    const showAdjustmentNote = ADJUSTMENT_NOTE_REQUIRES.every(key =>
      costItems.some(item => item.key === key && getAmount(item) > 0)
    );

    const getNote = item =>
      showAdjustmentNote && item.key === ADJUSTMENT_NOTE_ON ? (
        <Tooltip title={t('Includes adjustment rate')}>
          <Icon
            type="info-circle"
            style={{
              marginLeft: 4,
              fontSize: 12,
              color: Colors.grayMedium,
              cursor: 'pointer'
            }}
          />
        </Tooltip>
      ) : null;

    const showChart = costItems.length > 1;
    const chartData = showChart && {
      labels: costItems.map(item => t(ITEM_LABELS[item.key] || item.key)),
      datasets: [
        {
          data: costItems.map(getAmount),
          backgroundColor: costItems.map(item => getConceptColor(item.key)),
          borderWidth: 0
        }
      ]
    };
    const chartTotal = formatAmount(data.total_price_with_profit, resolvedCurrency);
    const centerHoleSize = DONUT_SIZE * 0.72;

    const totalsBlock =
      taxItems.length > 0 ? (
        <>
          <BreakdownRow
            label={t('Subtotal without taxes')}
            amount={data.total_price_with_profit}
            currency={resolvedCurrency}
            bold
            separator
          />
          {taxItems.map((item, index) => (
            <BreakdownRow
              key={`${item.key}-${index}`}
              label={t(ITEM_LABELS[item.key] || item.key)}
              amount={item.amount}
              percentage={getPercentage(item)}
              currency={resolvedCurrency}
              separator={index === 0}
            />
          ))}
          <BreakdownRow
            label={t('Total with taxes')}
            amount={data.total_price_with_tax}
            currency={resolvedCurrency}
            bold
            separator
          />
        </>
      ) : (
        <BreakdownRow
          label={t('Total')}
          amount={data.total_price_with_tax}
          currency={resolvedCurrency}
          bold
          separator
        />
      );

    return (
      <div style={{ minWidth: showChart ? 460 : 240, fontSize: 13 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>
          {t('Price breakdown')}
        </div>

        {showChart ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <div style={{ position: 'relative', width: DONUT_SIZE, height: DONUT_SIZE, flexShrink: 0 }}>
              <Doughnut data={chartData} options={donutOptions} />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: centerHoleSize,
                  textAlign: 'center',
                  lineHeight: 1.4
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    fontSize: 12,
                    color: Colors.grayMedium,
                    pointerEvents: 'none'
                  }}
                >
                  {t('Subtotal')}
                  <Tooltip
                    title={t('This chart does not include taxes')}
                    overlayStyle={{ maxWidth: 'none' }}
                    overlayInnerStyle={{ whiteSpace: 'nowrap' }}
                  >
                    <Icon
                      type="info-circle"
                      style={{ fontSize: 12, cursor: 'pointer', pointerEvents: 'auto' }}
                    />
                  </Tooltip>
                </div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: Colors.black,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none'
                  }}
                >
                  {chartTotal}
                </div>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              {costItems.map(item => (
                <BreakdownRow
                  key={item.key}
                  dotColor={getConceptColor(item.key)}
                  label={t(ITEM_LABELS[item.key] || item.key)}
                  amount={item.amount}
                  percentage={getPercentage(item)}
                  currency={resolvedCurrency}
                  note={getNote(item)}
                  spacious
                />
              ))}

              {totalsBlock}
            </div>
          </div>
        ) : (
          <>
            {costItems.map(item => (
              <BreakdownRow
                key={item.key}
                label={t(ITEM_LABELS[item.key] || item.key)}
                amount={item.amount}
                percentage={getPercentage(item)}
                currency={resolvedCurrency}
                note={getNote(item)}
              />
            ))}

            {totalsBlock}
          </>
        )}
      </div>
    );
  };

  return (
    <Popover
      trigger="hover"
      placement="bottom"
      content={renderContent()}
      onVisibleChange={handleVisibleChange}
    >
      <Icon
        type="question-circle"
        theme="filled"
        style={{ color: Colors.primaryAccent, fontSize: 16, marginLeft: 6, cursor: 'pointer' }}
      />
    </Popover>
  );
}

export default (PriceBreakdownTooltip);
